import React, { useCallback, useContext, useRef } from 'react';
import { Form, FormSpy } from 'react-final-form';
import { createForm } from 'final-form';
import { Grid, makeStyles } from '@material-ui/core';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import { isEmpty, omit } from 'lodash';
import { validateInvestorIdRff } from '../utils';
import { Select, TextField } from 'mui-rff';
import { fieldLengthValidator } from '../../../core/utils';
import InvestorApi from '../../../core/apis/InvestorApi';
import { CreationInvestorContext } from './CreationInvestorContext';
import { STEP } from '../InvestorCreate';

const investorNameMaxLength = 256;
const investorIdMaxLength = 20;
const investorAkaMaxLength = 255;
const investorTaxIdMaxLength = 50;

const useStyle = makeStyles({
  formGroup: {
    marginBlock: '16px'
  }
});

const InvestorDetailsStep = ({
  onSubmitEnd,
  setIsNextAvailable,
  setActiveListOfSteps,
  activeListOfSteps
}) => {
  const classes = useStyle();
  const { investorContext, setInvestorContext } = useContext(
    CreationInvestorContext
  );
  const notifyServerError = useNotifyServerError();
  const formStateRef = useRef({});

  const initialFormValues = isEmpty(investorContext.investor)
    ? null
    : investorContext.investor;

  const onSubmit = useCallback(
    async formValues => {
      const pristine = formStateRef.current.pristine;
      // If it's the first submission, we make a POST request.
      if (!pristine && investorContext.investor === undefined) {
        try {
          const response = await InvestorApi.create(formValues.id, formValues);
          setInvestorContext({ ...investorContext, investor: response });
          setIsNextAvailable(true);
          onSubmitEnd();
        } catch (e) {
          notifyServerError(e, undefined);
        }
      }
      // If the investor has already been created before then we make a PUT request.
      if (!pristine && investorContext.investor !== undefined) {
        const investorId = investorContext.investor.id;
        const payload = omit(formValues, 'id');
        try {
          const response = await InvestorApi.updateInvestor(
            investorId,
            payload
          );
          setInvestorContext({ ...investorContext, investor: response });
          setIsNextAvailable(true);
          onSubmitEnd();
        } catch (e) {
          notifyServerError(e, undefined);
        }
      }
      // If the user has already been created and there is no changes on the form, we move to next step.
      if (pristine && investorContext.investor !== undefined) {
        onSubmitEnd();
      }
      const updatedListOfStep = activeListOfSteps.map(step => {
        if (step.name === STEP.investorDetails) {
          return { ...step, completed: true };
        }
        return step;
      });
      setActiveListOfSteps(updatedListOfStep);
    },
    [
      investorContext,
      activeListOfSteps,
      setActiveListOfSteps,
      setInvestorContext,
      setIsNextAvailable,
      onSubmitEnd,
      notifyServerError
    ]
  );

  const formRef = useRef(
    createForm({
      onSubmit: onSubmit,
      initialValues: initialFormValues
    })
  );

  const required = value => (value ? undefined : 'Required');

  const composeValidators = (...validators) => value =>
    validators.reduce(
      (error, validator) => error || validator(value),
      undefined
    );

  return (
    <Form form={formRef.current}>
      {({ handleSubmit, submitting }) => (
        <>
          <FormSpy
            subscription={{ pristine: true, valid: true, submitting: true }}
            onChange={props => {
              setInvestorContext({
                ...investorContext,
                detailsFormState: props
              });
              formStateRef.current = { pristine: props.pristine };
            }}
          />
          <form onSubmit={handleSubmit} id="investorDetailsForm" noValidate>
            <Grid container>
              <Grid item xs={12} className={classes.formGroup}>
                <TextField
                  name="name"
                  label="Investor Name"
                  fieldProps={{
                    validate: composeValidators(
                      fieldLengthValidator(investorNameMaxLength),
                      required
                    )
                  }}
                  disabled={submitting}
                  required={true}
                />
              </Grid>

              <Grid item xs={12} className={classes.formGroup}>
                <TextField
                  name="aka"
                  label="A.K.A."
                  fieldProps={{
                    validate: fieldLengthValidator(investorAkaMaxLength)
                  }}
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12} className={classes.formGroup}>
                <TextField
                  name="id"
                  label="Investor ID"
                  fieldProps={{
                    validate: composeValidators(
                      fieldLengthValidator(investorIdMaxLength),
                      required,
                      validateInvestorIdRff
                    )
                  }}
                  disabled={submitting || !isEmpty(investorContext.investor)}
                  required={true}
                />
              </Grid>

              <Grid item xs={12} className={classes.formGroup}>
                <TextField
                  name="taxId"
                  label="Tax ID"
                  fieldProps={{
                    validate: fieldLengthValidator(investorTaxIdMaxLength)
                  }}
                  disabled={submitting}
                />
              </Grid>

              <Grid item xs={12} className={classes.formGroup}>
                <Select
                  name="status"
                  data={[
                    {
                      label: 'Active',
                      value: 'ACTIVE'
                    },
                    {
                      label: 'Inactive',
                      value: 'INACTIVE'
                    }
                  ]}
                  label="Status"
                  fieldProps={{
                    validate: required
                  }}
                  disabled={submitting}
                  required={true}
                />
              </Grid>
            </Grid>
          </form>
        </>
      )}
    </Form>
  );
};

export default InvestorDetailsStep;
